import { useEffect, useState } from 'react';
import type { ErpConnectionTest, ErpConnectionView, ErpPriceSource, ErpStockQuantity, ErpWarehouse } from '../../api';
import { erpApi } from '../../api';
import { LevelBadge, PanelCard, SectionHeader } from '../project/ui';
import { ConfirmDialog, ErrorNotice } from '../ui';
import { formatWhen } from '../distribution/labels';
import { Timestamp } from '../status/StatusPieces';
import {
  formatNumber,
  operationLabel,
  priceSourceHelp,
  priceSourceLabel,
  stockQuantityHelp,
  stockQuantityLabel,
  supportLabel,
  supportLevel,
  linkedWebsites,
  severalStores,
  websiteLabel,
} from './labels';
import { useStoreCopy } from './storeCopy';
import { useAction } from './useAction';
import { Checkbox, Field, Input, Select, Table } from '../primitives';

/**
 * The connection's settings in four groups (CO-158): the connection (name,
 * address, key, test), the sync policy, the warehouses the ERP reports, and
 * advanced details; removing the connection comes last. The key is
 * write-only: once stored it is never shown again, only whether one is.
 *
 * How products match, which warehouse feeds which inventory source and
 * whether the ERP's prices include VAT are each store's own settings
 * (CO-79), shown per website in Stores.
 */
export function ErpSettingsPanel({
  connection,
  canManage,
  onChanged,
  onDeleted,
}: {
  connection: ErpConnectionView;
  canManage: boolean;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const provider = connection.provider_info;
  const action = useAction();
  // The access panel reports its own results, next to the controls that
  // caused them.
  const access = useAction();
  const words = useStoreCopy();
  const [key, setKey] = useState('');
  const [test, setTest] = useState<ErpConnectionTest | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [name, setName] = useState(connection.name);
  const [interval, setInterval] = useState(String(connection.stock_interval_minutes));
  const [quantity, setQuantity] = useState<ErpStockQuantity>(connection.settings.stock_quantity);
  const [hold, setHold] = useState(String(connection.settings.price_change_hold_percent));
  const [priceSource, setPriceSource] = useState<ErpPriceSource>(connection.settings.price_source ?? 'stock_report');
  const [priceList, setPriceList] = useState(connection.settings.price_list || '1');
  const [enabled, setEnabled] = useState(connection.enabled);
  const providerFields = provider.settings ?? [];
  const storedProvider = (key: string) => {
    const value = connection.provider_settings?.[key];
    return typeof value === 'string' ? value : '';
  };
  const [providerValues, setProviderValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(providerFields.map((field) => [field.key, storedProvider(field.key)])),
  );
  const providerDirty = providerFields.some((field) => (providerValues[field.key] ?? '') !== storedProvider(field.key));

  // A reload of the connection resets the form to what is stored.
  useEffect(() => {
    setName(connection.name);
    setInterval(String(connection.stock_interval_minutes));
    setQuantity(connection.settings.stock_quantity);
    setHold(String(connection.settings.price_change_hold_percent));
    setPriceSource(connection.settings.price_source ?? 'stock_report');
    setPriceList(connection.settings.price_list || '1');
    setEnabled(connection.enabled);
    setProviderValues(
      Object.fromEntries(
        (connection.provider_info.settings ?? []).map((field) => {
          const value = connection.provider_settings?.[field.key];
          return [field.key, typeof value === 'string' ? value : ''];
        }),
      ),
    );
  }, [connection]);

  // The last read's warehouses, or the ones a test just listed before any
  // read has run.
  const warehouses: ErpWarehouse[] =
    connection.warehouses.length > 0 ? connection.warehouses : (test?.report?.warehouses ?? []);
  // The places this ERP can give sale prices from. A choice is offered only
  // when there is one to make.
  const offeredSources = provider.price_sources ?? [];
  const storedSource = connection.settings.price_source ?? 'stock_report';
  const intervalValue = Number(interval);
  const holdValue = Number(hold);
  const intervalValid = Number.isInteger(intervalValue) && intervalValue >= 5 && intervalValue <= 1440;
  const holdValid = Number.isInteger(holdValue) && holdValue >= 0 && holdValue <= 1000;

  const nameDirty = name.trim() !== connection.name;
  const saveConnection = () =>
    access.run(
      'details',
      async () => {
        await erpApi.updateConnection(connection.id, {
          ...(nameDirty ? { name: name.trim() } : {}),
          ...(providerDirty
            ? { provider_settings: Object.fromEntries(providerFields.map((field) => [field.key, (providerValues[field.key] ?? '').trim()])) }
            : {}),
        });
        onChanged();
      },
      providerDirty ? 'Saved. Test the connection to confirm the ERP answers at the new address.' : 'Saved.',
    );

  const save = () =>
    action.run(
      'save',
      async () => {
        await erpApi.updateConnection(connection.id, {
          enabled,
          stock_interval_minutes: intervalValue,
          stock_quantity: quantity,
          price_change_hold_percent: holdValue,
          ...(offeredSources.length > 1 ? { price_source: priceSource, price_list: priceList } : {}),
        });
        onChanged();
      },
      'Sync settings saved.',
    );

  const linked = linkedWebsites(connection);
  const withStore = severalStores(linked);

  return (
    <>
      <PanelCard labelledBy="erp-credential-title" testId="erp-credential">
        <SectionHeader
          id="erp-credential-title"
          title="Connection"
          help={
            <p>
              Commerce Ops keeps the {provider.display_name} key encrypted and uses it only to read stock and prices. Once
              stored it is never shown again, and stores never receive it.
            </p>
          }
          helpLabel="How the access key is kept"
        />
        <dl className="erp-facts">
          <div>
            <dt>Provider</dt>
            <dd>{provider.display_name || connection.provider}</dd>
          </div>
          <div>
            <dt>{provider.company_identifier_label || 'Company'}</dt>
            <dd>{connection.company_identifier}</dd>
          </div>
          {/* An address someone can edit is shown in its field below; the
              default one in use is shown here, since its field is empty. */}
          {providerFields.filter((field) => !canManage || !storedProvider(field.key)).map((field) => (
            <div key={field.key}>
              <dt>{field.label}</dt>
              <dd>
                <code className="erp-copy">{storedProvider(field.key) || field.default}</code>
                {storedProvider(field.key) ? null : <span className="muted"> (default)</span>}
              </dd>
            </div>
          ))}
          <div>
            <dt>{provider.credential_label || 'Access key'}</dt>
            <dd data-testid="erp-credential-state">{connection.credential_configured ? 'Stored' : <span className="erp-missing">Not stored yet</span>}</dd>
          </div>
          <div>
            <dt>Last connection test</dt>
            <dd>{connection.last_tested_at ? <Timestamp iso={connection.last_tested_at} /> : 'Never'}</dd>
          </div>
        </dl>
        {canManage ? (
          <form
            className="erp-form-grid"
            onSubmit={(event) => {
              event.preventDefault();
              void saveConnection();
            }}
          >
            <Field label="Connection name">
              <Input id="erp-name" autoComplete="off" maxLength={120} value={name} onChange={(event) => setName(event.target.value)} />
            </Field>
            {providerFields.map((field) => (
              <Field key={field.key} label={field.label} hint={field.help || undefined}>
                <Input
                  id={`erp-provider-${field.key}`}
                  type="url"
                  inputMode="url"
                  autoComplete="off"
                  spellCheck={false}
                  value={providerValues[field.key] ?? ''}
                  placeholder={field.placeholder ? `${field.placeholder}` : undefined}
                  onChange={(event) => setProviderValues({ ...providerValues, [field.key]: event.target.value })}
                />
              </Field>
            ))}
            <div className="erp-form-grid__actions">
              <button
                type="submit"
                className="button button--primary"
                disabled={(!nameDirty && !providerDirty) || !name.trim() || access.busy !== null}
              >
                {access.busy === 'details' ? 'Saving' : 'Save connection details'}
              </button>
            </div>
          </form>
        ) : null}
        {canManage ? (
          <form
            className="erp-inline-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (!key.trim()) return;
              void access.run(
                'credential',
                async () => {
                  await erpApi.setCredential(connection.id, key.trim());
                  setKey('');
                  onChanged();
                },
                'Key stored. Test the connection to use it.',
              );
            }}
          >
            <Field
              label={connection.credential_configured ? 'Replace the key' : provider.credential_label || 'Access key'}
              hint={provider.credential_hint || undefined}
            >
              <Input
                id="erp-key"
                type="password"
                autoComplete="off"
                spellCheck={false}
                value={key}
                onChange={(event) => setKey(event.target.value)}
                placeholder={connection.credential_configured ? 'Paste a new key to replace the stored one…' : 'Paste the key…'}
              />
            </Field>
            <button type="submit" className="button button--secondary" disabled={!key.trim() || access.busy !== null}>
              {access.busy === 'credential' ? 'Storing' : 'Store key'}
            </button>
            <button
              type="button"
              className="button button--secondary"
              disabled={!connection.credential_configured || access.busy !== null}
              data-testid="erp-test"
              onClick={() =>
                void access.run('test', async () => {
                  const result = await erpApi.testConnection(connection.id);
                  setTest(result);
                  onChanged();
                  return result;
                })
              }
            >
              {access.busy === 'test' ? 'Testing' : 'Test connection'}
            </button>
          </form>
        ) : null}
        <ErrorNotice error={access.error} />
        {access.notice ? (
          <p className="panel__note" role="status">
            {access.notice}
          </p>
        ) : null}
        {test ? (
          <div
            className={`panel__note ${test.ok ? '' : 'panel__note--danger'}`}
            role="status"
            data-testid="erp-test-result"
          >
            <strong>{test.ok ? 'The ERP answered.' : 'The test failed.'}</strong> {test.message}
            {test.detail ? <span className="erp-detail">{test.detail}</span> : null}
          </div>
        ) : null}
      </PanelCard>

      <PanelCard labelledBy="erp-reading-title" testId="erp-sync-policy">
        <SectionHeader
          id="erp-reading-title"
          title="Sync policy"
          help={
            <p>
              Commerce Ops never writes to the ERP. Matching products, mapping warehouses, whether the ERP’s prices include
              VAT, and overwriting or only filling prices are set in each store: the Stores tab shows what each website
              chose.
            </p>
          }
          helpLabel="What is set here and what in the store"
        />
        <div className="erp-settings-grid">
          <Field
            label="Sync interval (minutes)"
            error={intervalValid ? null : 'Enter a whole number from 5 to 1440.'}
          >
            <Input
              id="erp-interval"
              type="number"
              inputMode="numeric"
              min={5}
              max={1440}
              value={interval}
              onChange={(event) => setInterval(event.target.value)}
              disabled={!canManage}
            />
          </Field>
          <Field label="Stock sent to stores" hint={<>{stockQuantityHelp[quantity]}</>}>
            <Select
              id="erp-quantity"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value as ErpStockQuantity)}
              disabled={!canManage}
            >
              {(Object.keys(stockQuantityLabel) as ErpStockQuantity[]).map((value) => (
                <option key={value} value={value}>
                  {stockQuantityLabel[value]}
                </option>
              ))}
            </Select>
          </Field>
          {offeredSources.length > 1 ? (
            <Field label="Sale prices come from" hint={<>{priceSourceHelp[priceSource]}</>}>
              <Select
                id="erp-price-source"
                value={priceSource}
                onChange={(event) => setPriceSource(event.target.value as ErpPriceSource)}
                disabled={!canManage}
              >
                {offeredSources.map((value) => (
                  <option key={value} value={value}>
                    {priceSourceLabel[value]}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          {offeredSources.length > 1 && priceSource === 'article_card' ? (
            <Field
              label="Price list used as the shop price"
              hint="Both lists are kept, so changing this needs no new read."
            >
              <Select id="erp-price-list" value={priceList} onChange={(event) => setPriceList(event.target.value)} disabled={!canManage}>
                <option value="1">Price list 1</option>
                <option value="2">Price list 2</option>
              </Select>
            </Field>
          ) : null}
          <Field
            label="Hold price changes larger than (%)"
            hint={holdValid ? 'A bigger change waits in Issues until you accept it. 0 sends every change.' : undefined}
            error={holdValid ? null : 'Enter a whole number from 0 to 1000.'}
          >
            <Input
              id="erp-hold"
              type="number"
              inputMode="numeric"
              min={0}
              max={1000}
              value={hold}
              onChange={(event) => setHold(event.target.value)}
              disabled={!canManage}
            />
          </Field>
          <Checkbox
            id="erp-enabled"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
            disabled={!canManage}
            label="Sync stock on schedule"
            hint="When off, nothing is read and stores keep their current stock and prices."
          />
        </div>
        {priceSource === 'article_card' && storedSource !== 'article_card' ? (
          <p className="panel__note" data-testid="erp-price-source-note">
            Prices are read a few articles at a time after each stock read, so the first ones reach a store within the hour
            and all of them after a few reads. A store only receives prices if it has price sync on, and a price that moves by
            more than the hold threshold waits in Issues until you accept it.
          </p>
        ) : null}
        <ErrorNotice error={action.error} />
        {action.notice ? (
          <p className="panel__note" role="status">
            {action.notice}
          </p>
        ) : null}
        {canManage ? (
          <div className="erp-actions">
            <button
              type="button"
              className="button button--primary"
              disabled={action.busy !== null || !intervalValid || !holdValid}
              onClick={() => void save()}
              data-testid="erp-save-settings"
            >
              {action.busy === 'save' ? 'Saving' : 'Save sync settings'}
            </button>
          </div>
        ) : null}
      </PanelCard>

      <PanelCard labelledBy="erp-warehouses-title" testId="erp-warehouses">
        <SectionHeader
          id="erp-warehouses-title"
          title="Warehouses"
          help={<p>Each store maps the warehouses it sells from in {words.admin}: {words.settingsPath}.</p>}
          helpLabel="Where warehouses are mapped"
          actions={
            canManage ? (
              <button
                type="button"
                className="button button--secondary button--small"
                disabled={!connection.credential_configured || action.busy !== null}
                onClick={() =>
                  void action.run('test', async () => {
                    const result = await erpApi.testConnection(connection.id);
                    setTest(result);
                    onChanged();
                    return result;
                  })
                }
              >
                {action.busy === 'test' ? 'Asking the ERP' : 'List warehouses'}
              </button>
            ) : null
          }
        />
        {warehouses.length > 0 ? (
          <Table
            label="Warehouses the ERP reports"
            compact
            className="erp-table"
            tableTestId="erp-warehouse-list"
          >
            <thead>
              <tr>
                <th scope="col">Code</th>
                <th scope="col">Name</th>
                <th scope="col" className="erp-table__num">
                  Articles
                </th>
                <th scope="col">Mapped by</th>
              </tr>
            </thead>
            <tbody>
              {warehouses.map((w) => {
                const mappedBy = linked.filter((entry) =>
                  (entry.website.erp.locations ?? []).some((row) => row.erp_warehouse === w.code),
                );
                return (
                  <tr key={w.code}>
                    <th scope="row">
                      <code>{w.code}</code>
                    </th>
                    <td>{w.name || <span className="muted">No name</span>}</td>
                    <td className="erp-table__num tabular-nums">{formatNumber(w.articles)}</td>
                    <td>
                      {mappedBy.length > 0 ? (
                        mappedBy
                          .map((entry) => {
                            const row = (entry.website.erp.locations ?? []).find((r) => r.erp_warehouse === w.code);
                            return `${websiteLabel(entry.store, entry.website, withStore)} → ${row?.location ?? ''}`;
                          })
                          .join(', ')
                      ) : (
                        <span className="muted">No store</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : (
          <p className="quiet-empty">
            {connection.credential_configured ? 'No warehouses yet' : 'Store the access key first'}
          </p>
        )}
      </PanelCard>

      <PanelCard labelledBy="erp-advanced-title" testId="erp-advanced">
        <SectionHeader id="erp-advanced-title" title="Advanced details" />
        <dl className="erp-facts">
          <div>
            <dt>API version Commerce Ops reads</dt>
            <dd>{provider.api_version || <span className="muted">Not known</span>}</dd>
          </div>
        </dl>
        <details className="erp-disclosure" data-testid="erp-capabilities">
          <summary>Provider capabilities</summary>
          <Table label={`Capabilities of ${provider.display_name}`} compact className="erp-table">
            <thead>
              <tr>
                <th scope="col">Operation</th>
                <th scope="col">Support</th>
                <th scope="col">In Commerce Ops</th>
                <th scope="col">Notes</th>
              </tr>
            </thead>
            <tbody>
              {provider.capabilities.map((capability) => (
                <tr key={capability.operation}>
                  <th scope="row">{operationLabel[capability.operation] ?? capability.operation}</th>
                  <td>
                    <LevelBadge level={supportLevel[capability.support]}>{supportLabel[capability.support]}</LevelBadge>
                  </td>
                  <td>
                    {capability.implemented
                      ? connection.verified_capabilities[capability.operation]
                        ? `Used, verified ${formatWhen(connection.verified_capabilities[capability.operation])}`
                        : 'Used'
                      : 'Not used'}
                  </td>
                  <td className="erp-table__note">{capability.note}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </details>
      </PanelCard>

      {canManage ? (
        <PanelCard labelledBy="erp-danger-title" tone="warning" testId="erp-danger">
          <SectionHeader
            id="erp-danger-title"
            title="Remove this connection"
            actions={
              <button type="button" className="button button--danger-quiet" onClick={() => setConfirmDelete(true)}>
                Remove connection
              </button>
            }
          />
        </PanelCard>
      ) : null}

      <ConfirmDialog
        open={confirmDelete}
        title={`Remove ${connection.name}?`}
        description="The access key, the stock read so far and the history of this connection are deleted. Stores keep what they have. This cannot be undone."
        confirmLabel="Remove connection"
        danger
        busy={action.busy === 'delete'}
        confirmText={connection.name}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() =>
          void action.run('delete', async () => {
            await erpApi.deleteConnection(connection.id);
            setConfirmDelete(false);
            onDeleted();
          })
        }
      />
    </>
  );
}
